import { toast } from "sonner";
import { flushPendingSaves } from "@/lib/api/clients";

/**
 * Registriert den Service Worker und bietet neue Versionen zum Nachladen an.
 *
 * Nur in gebauten Bundles: im Vite-Dev-Server würde ein cachender Worker mit
 * HMR kollidieren, und dist/sw.js entsteht ohnehin erst beim Build.
 */

let reloadingForUpdate = false;

/**
 * Übernimmt den wartenden Worker. Der eigentliche Reload passiert erst im
 * "controllerchange"-Handler, sobald der neue Worker die Seite kontrolliert.
 */
async function applyUpdate(waiting: ServiceWorker): Promise<void> {
  if (reloadingForUpdate) return;
  reloadingForUpdate = true;
  // Dieselbe Absicherung wie im beforeunload-Handler in src/main.tsx: debounced
  // Speichervorgänge müssen raus, bevor die Seite neu lädt.
  await flushPendingSaves().catch(() => undefined);
  waiting.postMessage({ type: "SKIP_WAITING" });
}

function announceUpdate(waiting: ServiceWorker): void {
  toast("Neue Version verfügbar", {
    description: "Lade die Seite neu, um die Aktualisierung zu übernehmen.",
    duration: Number.POSITIVE_INFINITY,
    action: {
      label: "Neu laden",
      onClick: () => {
        void applyUpdate(waiting);
      }
    }
  });
}

function watchForUpdate(registration: ServiceWorkerRegistration): void {
  // Beim ersten Besuch gibt es keinen Controller — der frisch installierte
  // Worker ist dann kein Update, sondern der Normalzustand.
  if (registration.waiting && navigator.serviceWorker.controller) {
    announceUpdate(registration.waiting);
  }

  registration.addEventListener("updatefound", () => {
    const installing = registration.installing;
    if (!installing) return;
    installing.addEventListener("statechange", () => {
      if (installing.state === "installed" && navigator.serviceWorker.controller) {
        announceUpdate(installing);
      }
    });
  });
}

export function registerServiceWorker(): void {
  if (!__SERVICE_WORKER_BUILD__) return;
  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!reloadingForUpdate) return;
    window.location.reload();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then(watchForUpdate)
      .catch(() => {
        // Ein fehlgeschlagener Worker darf die Anwendung nicht beeinträchtigen:
        // ohne ihn läuft alles wie bisher, nur ohne Offline-Fallback.
      });
  });
}
